import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ArrowUpRight } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import contaminationFlooringCardImg from "@/assets/contamination-control-flooring/card-wide.webp";
import pvcCardImg from "@/assets/home-industries/healthcare.webp";
import wallsCardImg from "@/assets/services/pvc-card.webp";
import refurbCardImg from "@/assets/services/hosp-refub.webp";
import glassCardImg from "@/assets/services/glass-card.webp";

// Expanding-card panel adapted from codepen.io/rachsmith/pen/PWxoLN
// - 2-column layout with the right column offset down (staggered rhythm)
// - Coloured cards, thin outline border, image overhanging the top-left
// - Click a card → uses Framer Motion `layoutId` to expand into a
//   full-screen detail overlay. Close button returns.

const ITEMS = [
  {
    id: "pvc",
    slug: "antimicrobial-pvc-wall-cladding",
    title: "Antimicrobial PVC Cladding",
    kicker: "Silver-ion PVC",
    color: "#189A96",
    image: pvcCardImg,
    body: [
      "Our flagship silver-ion PVC system — the same panels used in NABH corridors, sterile fill-finish rooms and BSL laboratories. Bound at the molecular level, the antimicrobial performance stays measurable for the full fourteen-year warranty.",
      "Thermoformed corners, heat-welded joints and colour-matched trim eliminate every geometric harbour where surface pathogens colonise. ISO 22196 tested, Class 1 fire rated, wipe-down finish.",
    ],
    tags: ["ISO 22196", "Class 1 fire", "Silver-ion bound", "14yr warranty"],
  },
  {
    id: "walls",
    slug: "hygienic-wall-cladding",
    title: "Hygienic Wall, Floor & Ceiling Cladding",
    kicker: "Monolithic system",
    color: "#14807E",
    image: wallsCardImg,
    body: [
      "A single continuous specification from floor coving, up the wall, and across the ceiling — engineered as one system, sealed at every junction, wipe-down at every touch point.",
      "Ideal for operating theatres, cleanrooms and food-processing zones where inspectors trace every millimetre and cleaning teams need surfaces that survive daily chemical cycles.",
    ],
    tags: ["GMP", "USP 797", "Seamless", "Chemical resistant"],
  },
  {
    id: "flooring",
    slug: "contamination-control-flooring",
    title: "Contamination Control Flooring",
    kicker: "Entrance-zone flooring",
    color: "#0F2A44",
    image: contaminationFlooringCardImg,
    body: [
      "Permanent flooring engineered to capture and retain contaminants from footwear and wheeled equipment before they reach controlled spaces.",
      "Reusable and easy to clean as part of routine housekeeping — no disposable adhesive layers to replace, no loss of effectiveness over time.",
    ],
    tags: ["Entrance-zone", "Reusable", "Low-maintenance"],
  },
  {
    id: "glass",
    slug: "printed-glass-wall",
    title: "Printed Glass Interiors",
    kicker: "Optically clear",
    color: "#189A96",
    image: glassCardImg,
    body: [
      "Toughened, back-painted glass panels for the front-of-house and observation zones where the hygienic surface still has to be beautiful.",
      "Bonded to a fully sealed sub-frame — no visible fixings, no silicone lines to fail. Available in the full antimicrobial palette or as a custom colour match.",
    ],
    tags: ["Toughened", "Back-painted", "Zero fixings", "Bespoke colour"],
  },
  {
    id: "refurb",
    slug: "hospital-refurbishment",
    title: "Hospital Refurbishment",
    kicker: "Live-site methodology",
    color: "#14807E",
    image: refurbCardImg,
    body: [
      "Phased, live-site installation methodology developed over a decade of retrofitting operational wards, ICUs and OT suites — without the ward closing.",
      "Sectional shutdown, negative-pressure containment, dust-free demolition and single-shift handovers. Your infection prevention lead approves the plan before we mobilise.",
    ],
    tags: ["Live-site", "Phased", "IP-approved", "Negative pressure"],
  },
];

// 2-column staggered layout: right column offset-down. Distribute so
// column-A gets items 0, 2, 4 and column-B gets 1, 3.
const columnA = [ITEMS[0], ITEMS[2], ITEMS[4]];
const columnB = [ITEMS[1], ITEMS[3]];

function Card({ item, onClick }) {
  const navigate = useNavigate();
  return (
    <motion.article
      layoutId={`card-${item.id}`}
      onClick={() =>
        item.slug ? navigate(`/services/${item.slug}`) : onClick(item)
      }
      whileHover={{ y: -6 }}
      transition={{ duration: 0.35, ease: [0.19, 1, 0.22, 1] }}
      data-testid={`install-card-${item.id}`}
      className="relative cursor-pointer group"
      style={{ backgroundColor: item.color, color: "#F8FAFC" }}
    >
      {/* Offset outline border — hallmark of the reference design */}
      <div
        aria-hidden
        className="absolute pointer-events-none border border-white/40 transition-transform duration-500 group-hover:scale-[1.04]"
        style={{
          top: "-8px",
          left: "-8px",
          right: "8px",
          bottom: "8px",
        }}
      />

      {/* Overhanging image at top */}
      <motion.div
        layoutId={`card-image-${item.id}`}
        className="relative aspect-[5/3] w-[92%] -mt-6 -ml-2 overflow-hidden"
      >
        <img
          src={item.image}
          alt={item.title}
          className="w-full h-full transition-transform duration-700 group-hover:scale-[1.06]"
        />
      </motion.div>

      {/* Copy block */}
      <div className="px-6 md:px-8 pt-8 pb-10">
        {/* <motion.p
          layoutId={`card-kicker-${item.id}`}
          className="overline text-white/80"
        >
          {item.kicker}
        </motion.p> */}
        <motion.h3
          layoutId={`card-title-${item.id}`}
          className="font-serif text-3xl md:text-[2rem] mt-4 leading-[1.1] text-white pr-6"
        >
          {item.title}
        </motion.h3>

        <div className="mt-8 flex items-center justify-between">
          <span className="text-[0.68rem] uppercase tracking-[0.16em] font-sans font-semibold text-white/85">
            Open detail
          </span>
          <span className="w-9 h-9 rounded-full bg-white/15 flex items-center justify-center transition-all duration-300 group-hover:bg-white group-hover:text-[#0F2A44]">
            <ArrowUpRight className="w-4 h-4" />
          </span>
        </div>
      </div>
    </motion.article>
  );
}

function DetailOverlay({ item, onClose }) {
  useEffect(() => {
    document.body.style.overflow = "hidden";
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    };
  }, [onClose]);

  return (
    <motion.div
      layoutId={`card-${item.id}`}
      transition={{ duration: 0.55, ease: [0.19, 1, 0.22, 1] }}
      data-testid={`install-detail-${item.id}`}
      className="fixed inset-0 z-[100] overflow-y-auto"
      style={{ backgroundColor: item.color }}
    >
      {/* Close */}
      <button
        onClick={onClose}
        data-testid="install-detail-close"
        aria-label="Close detail"
        className="fixed top-6 right-6 md:top-8 md:right-10 z-10 w-12 h-12 rounded-full bg-white text-[#0F2A44] flex items-center justify-center hover:scale-110 transition-transform"
      >
        <X className="w-5 h-5" />
      </button>

      <div className="max-w-[1200px] mx-auto px-6 md:px-10 py-24 md:py-32">
        <motion.p
          layoutId={`card-kicker-${item.id}`}
          className="overline text-white/85"
        >
          {item.kicker}
        </motion.p>
        <motion.h2
          layoutId={`card-title-${item.id}`}
          className="font-serif text-5xl md:text-7xl mt-6 leading-[1.02] text-white max-w-4xl"
        >
          {item.title}
        </motion.h2>

        <motion.div
          layoutId={`card-image-${item.id}`}
          className="relative mt-14 aspect-[16/9] md:aspect-[21/9] overflow-hidden"
        >
          <img
            src={item.image}
            alt={item.title}
            className="w-full h-full object-cover"
          />
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.35, duration: 0.7, ease: [0.19, 1, 0.22, 1] }}
          className="mt-16 grid grid-cols-1 md:grid-cols-12 gap-10"
        >
          <div className="md:col-span-7 space-y-5">
            {item.body.map((p, i) => (
              <p
                key={i}
                className="font-sans text-lg md:text-xl text-white/90 leading-relaxed"
              >
                {p}
              </p>
            ))}
          </div>
          <div className="md:col-span-4 md:col-start-9">
            <p className="overline text-white/70 mb-6">Specifications</p>
            <div className="border-t border-white/25">
              {item.tags.map((tag) => (
                <div
                  key={tag}
                  className="py-4 border-b border-white/25 flex items-center justify-between text-white/95 font-sans text-sm"
                >
                  <span>{tag}</span>
                  <span className="text-white/50">✓</span>
                </div>
              ))}
            </div>
            <Link
              to="/contact"
              onClick={onClose}
              className="mt-10 inline-flex items-center gap-2 px-6 py-3 bg-white text-[#0F2A44] font-sans text-xs uppercase tracking-[0.16em] font-semibold hover:bg-[#F8FAFC] transition-colors"
              data-testid="install-detail-cta"
            >
              Request a quote <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
        </motion.div>
      </div>
    </motion.div>
  );
}

export default function InstallCards() {
  const [openItem, setOpenItem] = useState(null);

  return (
    <section
      data-testid="home-services"
      className="bg-[#F8FAFC] pt-10 md:pt-16 pb-6 md:pb-8 relative"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="mb-14">
          <p className="overline">What we install</p>
          <h2 className="font-serif text-5xl md:text-6xl mt-6 leading-[1.02]">
            Five systems, one{" "}
            <span className="italic text-[#189A96]">standard</span>.
          </h2>
        </div>

        {/* 2-column staggered cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-10">
          <div className="flex flex-col gap-6 md:gap-10">
            {columnA.map((item) => (
              <Card
                key={item.id}
                item={item}
                onClick={setOpenItem}
              />
            ))}
          </div>
          <div className="flex flex-col gap-6 md:gap-10 md:pt-16">
            {columnB.map((item) => (
              <Card
                key={item.id}
                item={item}
                onClick={setOpenItem}
              />
            ))}
          </div>
        </div>
      </div>

      <AnimatePresence>
        {openItem && (
          <DetailOverlay
            key={openItem.id}
            item={openItem}
            onClose={() => setOpenItem(null)}
          />
        )}
      </AnimatePresence>
    </section>
  );
}
